import { useEffect, useState } from 'react'
import { SURVEY_RESTRICTION_METHOD, SURVEY_TYPE as DOMAIN_SURVEY_TYPE } from '../../domain/domainCodes'
import { useNavigate, useParams } from 'react-router-dom'
import { ListChecks } from 'lucide-react'
import FormPage from '../../components/FormPage'
import useGoBack from '../../components/useGoBack'
import DateRangeField from '../../components/DateRangeField'
import { FormBody, FormCols, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import {
  SURVEY_TYPE,
  coursesForSurvey,
  getEngagementOptions,
  getSurvey,
  programsForSurvey,
  saveSurvey,
} from './engagementService'
import { surveysStrings as S } from './strings'
import './engagement.css'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  title: '',
  descriptionAr: '',
  titleEn: '',
  descriptionEn: '',
  enabled: true,
  type: String(DOMAIN_SURVEY_TYPE.GENERAL),
  restrictToGroups: false,
  groupIds: [],
  mandatory: false,
  restriction: String(SURVEY_RESTRICTION_METHOD.COURSE_AFTER_WATCHING_CONTENT),
  /* The window. Mapped in both directions by `engagementService` since it was written, with
     no control anywhere to set it — so `GetSurveysDataHandler`, which now enforces the
     period, was enforcing something the panel could not enter. */
  timeRestricted: false,
  startDate: '',
  endDate: '',
}

const STEPS = ['basics', 'groups', 'rules']

/**
 * استبيان جديد / تعديل — Views/Shared/Components/SurveyModal.
 *
 * The legacy is a three-step Bootstrap stepper and the steps are real: what the
 * survey IS, who it is for, and how it is entered. That structure was already
 * kept here; what changes is the container. Fourteen controls in a 560px sheet
 * is the case FormPage exists for, and it is the last of the legacy's five
 * wizards to leave the drawer.
 *
 * Validation runs per step, so an error on step one is raised before you are
 * three screens past it.
 */
export default function SurveyFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  /* Back to the page this form was opened from — the list as it was left,
     not its first page. See components/useGoBack.js. */
  const goBack = useGoBack('/surveys-management')
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [step, setStep] = useState(0)
  const [groups, setGroups] = useState([])
  const [targets, setTargets] = useState([])
  const [saving, setSaving] = useState(false)

  /* A course survey is attached to a course and a programme survey to a programme; a
     general one to neither. The API rejects any other combination, so the form asks for
     exactly what the chosen type needs. */
  const surveyType = Number(values.type ?? SURVEY_TYPE.general)
  const isGeneral = surveyType === SURVEY_TYPE.general
  const targetKey = surveyType === SURVEY_TYPE.course ? 'courseId' : 'programId'

  useEffect(() => {
    let alive = true
    getEngagementOptions().then((options) => alive && setGroups(options.groups ?? []))
    if (id) {
      getSurvey(id)
        .then((row) => {
          if (!alive) return
          /* A survey that did not load leaves the form on its defaults, which looks like a
             blank new one — so say so rather than let it be filled in and saved as a
             duplicate. */
          if (!row) return toast.error(S.actionFailed)
          setValues({ ...EMPTY, ...row })
        })
        .catch((error) => alive && toast.error(error?.message || S.actionFailed))
    }
    return () => {
      alive = false
    }
  }, [id, toast])

  useEffect(() => {
    if (isGeneral) {
      setTargets([])
      return undefined
    }
    let alive = true
    const load = surveyType === SURVEY_TYPE.course ? coursesForSurvey : programsForSurvey
    load().then((rows) => alive && setTargets(rows))
    return () => {
      alive = false
    }
  }, [surveyType, isGeneral])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const validateStep = (index) => {
    const next = {}
    if (index === 0) {
      if (!values.title.trim()) next.title = S.form.required
      if (!values.titleEn.trim()) next.titleEn = S.form.required
      if (!isGeneral && !values[targetKey]) next[targetKey] = S.form.required
    }
    if (index === 1 && values.restrictToGroups && values.groupIds.length === 0) {
      next.groupIds = S.form.required
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    setSaving(true)
    const outcome = await saveSurvey(values)
    if (!outcome.ok) {
      setSaving(false)
      return toast.error(outcome.message || S.actionFailed)
    }
    toast.success(serverText(outcome.message) || S.form.saved)
    navigate('/surveys-management')
  }

  return (
    <FormPage
      icon={ListChecks}
      title={id ? S.row.update : S.create}
      subtitle={S.title}
      steps={STEPS.map((key) => ({ id: key, label: S.form.steps[key] }))}
      step={step}
      onStep={setStep}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      {() => (
        <>
        {step === 0 && (
          <>
          {/* FormBody, because these are stacked form controls and something has to
              space them. `FormCols` sets its own gap and `SwitchField` carries none, so a
              switch under a grid sat flush against it — the gap belongs to the container,
              which is what this is. */}
            <FormBody>
            <FormCols>
              <FormRow label={S.form.nameAr} error={errors.title}>
                {(id) => (
                  <TextField
                    id={id}
                    value={values.title}
                    onChange={set('title')}
                    invalid={!!errors.title}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.nameEn} error={errors.titleEn}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.titleEn}
                    onChange={set('titleEn')}
                    invalid={!!errors.titleEn}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.descriptionAr}>
                {(id) => (
                  <Textarea
                    id={id}
                    rows={3}
                    value={values.descriptionAr}
                    onChange={set('descriptionAr')}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.descriptionEn}>
                {(id) => (
                  <Textarea
                    id={id}
                    dir="ltr"
                    rows={3}
                    value={values.descriptionEn}
                    onChange={set('descriptionEn')}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.type}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.type}
                    onChange={set('type')}
                    options={Object.entries(S.types).map(([value, label]) => ({ value, label }))}
                  />
                )}
              </FormRow>

              {/* Only for a course or programme survey — a general one is attached to
                  nothing, and the API refuses it if it is. */}
              {!isGeneral && (
                <FormRow
                  label={surveyType === SURVEY_TYPE.course ? S.form.course : S.form.program}
                  error={errors[targetKey]}
                >
                  {(id) => (
                    <Select
                      id={id}
                      value={values[targetKey] ?? ''}
                      onChange={set(targetKey)}
                      options={targets}
                      placeholder={S.form.chooseTarget}
                      invalid={!!errors[targetKey]}
                    />
                  )}
                </FormRow>
              )}
            </FormCols>

            <SwitchField
              checked={values.enabled}
              onChange={set('enabled')}
              label={S.form.enabled}
            />
            </FormBody>
          </>
        )}

        {step === 1 && (
          <>
          {/* FormBody, because these are stacked form controls and something has to
              space them. `FormCols` sets its own gap and `SwitchField` carries none, so a
              switch under a grid sat flush against it — the gap belongs to the container,
              which is what this is. */}
            <FormBody>
            <SwitchField
              checked={values.restrictToGroups}
              onChange={set('restrictToGroups')}
              label={S.form.restrictToGroups}
              hint={S.form.restrictHint}
            />

            {/* The picker only exists while the survey IS restricted — the
                legacy renders it always and ignores it when the toggle is off. */}
            {values.restrictToGroups && (
              <GroupPicker
                options={groups}
                selected={values.groupIds}
                onChange={set('groupIds')}
                legend={S.form.groups}
                error={errors.groupIds}
                selectAllLabel={S.form.selectAll}
                filterLabel={S.form.filter}
              />
            )}
            </FormBody>
          </>
        )}

        {step === 2 && (
          <>
          {/* FormBody, because these are stacked form controls and something has to
              space them. `FormCols` sets its own gap and `SwitchField` carries none, so a
              switch under a grid sat flush against it — the gap belongs to the container,
              which is what this is. */}
            <FormBody>
            {/* "After watching the content", "after finishing the exam" — every option
                names a moment in a course, so it is asked only when there is one. */}
            {!isGeneral && (
              <FormRow label={S.form.restriction}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.restriction ?? ''}
                    onChange={set('restriction')}
                    options={Object.entries(S.form.restrictions).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                )}
              </FormRow>
            )}

            <SwitchField
              checked={values.mandatory}
              onChange={set('mandatory')}
              label={S.form.mandatory}
            />

            {/* TURNING IT OFF CLEARS BOTH DATES, and that is correctness rather than tidiness.
                `Survey` has no `IsTimeRestricted` column — the flag lives only on the DTO — so
                the two dates ARE the window, and `GetSurveysDataHandler` applies them whatever
                the switch says. A survey left carrying dates from an abandoned window would go
                on being hidden from learners with nothing on this screen to explain why. */}
            <SwitchField
              checked={values.timeRestricted}
              onChange={(next) =>
                setValues((previous) => ({
                  ...previous,
                  timeRestricted: next,
                  ...(next ? null : { startDate: '', endDate: '' }),
                }))
              }
              label={S.form.timeRestricted}
              hint={S.form.timeRestrictedHint}
            />

            {/* Only when the survey IS limited. */}
            {values.timeRestricted && (
              <FormRow label={S.form.period}>
                {(id) => (
                  <DateRangeField
                    id={id}
                    value={{ from: values.startDate, to: values.endDate }}
                    onChange={(next) =>
                      setValues((previous) => ({
                        ...previous,
                        startDate: next.from ?? '',
                        endDate: next.to ?? '',
                      }))
                    }
                    labels={{ from: S.form.from, to: S.form.to }}
                  />
                )}
              </FormRow>
            )}
            </FormBody>
          </>
        )}
        </>
      )}
    </FormPage>
  )
}
